import * as geometries from "@/geometries";
import geofences from "@/geofences";
import { Geofence } from '@/types';

declare global {
    interface ErrorConstructor {
        captureStackTrace(thisArg: any, func: any): void;
    }
}

export class EvaluationError extends Error
{
    public node: Expression | null;
    public isEvaluationError: boolean;

    constructor(node: Expression | null, ...params: any[]) {
        super(...params);

        if (Error.captureStackTrace) Error.captureStackTrace(this, EvaluationError);

        this.node = node;
        this.isEvaluationError = true;
    }
}

export class Result
{
    public type: symbol;
    public value: symbol | boolean | GroupingEvaluation | geometries.Region | Result[];

    static edgeType = Symbol("Result.edgeType");
    static levelType = Symbol("Result.levelType");
    static geometryType = Symbol("Result.geometryType");
    static actionType = Symbol("Result.actionType");
    static groupingType = Symbol("Result.groupingType");
    static assetsType = Symbol("Result.assetsType");
    static emptyType = Symbol("Result.emptyType");
    static errorType = Symbol("Result.errorType");
    static spreadType = Symbol("Result.spreadType");

    static typeNames: Map<symbol, string> = new Map([
        [Result.edgeType, "edge"],
        [Result.levelType, "level"],
        [Result.geometryType, "geometry"],
        [Result.actionType, "action"],
        [Result.groupingType, "grouping"],
        [Result.assetsType, "assets"],
        [Result.emptyType, "empty"],
        [Result.errorType, "error"],
        [Result.spreadType, "spread"]
    ]);

    static pulsedValue = Symbol("Result.pulsed");
    static notPulsedValue = Symbol("Result.notPulsed");
    static assertedValue = Symbol("Result.asserted");
    static notAssertedValue = Symbol("Result.notAsserted");
    static assetIncluded = Symbol("Result.assetIncluded");
    static assetExcluded = Symbol("Result.assetExcluded");
    static assetUnmatched = Symbol("Result.assetUnmatched");
    static emptyValue = Symbol("Result.empty");

    static pulsed = new Result(Result.edgeType, Result.pulsedValue);
    static notPulsed = new Result(Result.edgeType, Result.notPulsedValue);
    static asserted = new Result(Result.levelType, Result.assertedValue);
    static notAsserted = new Result(Result.levelType, Result.notAssertedValue);
    static empty = new Result(Result.emptyType, Result.emptyValue);

    static fromGeometry: (geometry: any) => Result = (geometry) => new Result(Result.geometryType, new geometries.ComputedRegion(geometry));
    static geometryFromEmpty: () => Result = () => new Result(Result.geometryType, new geometries.EmptyRegion());
    static levelFrom: (value: any) => Result = (value) => new Result(Result.levelType, value ? Result.assertedValue : Result.notAssertedValue);
    static pulseFrom: (value: any) => Result = (value) => new Result(Result.edgeType, value ? Result.pulsedValue : Result.notPulsedValue);
    static assetFrom: (value: any) => Result = (value) => new Result(Result.assetsType, value ? Result.assetIncluded : Result.assetUnmatched);

    constructor(type: symbol, value: any)
    {
        this.type = type;
        this.value = value;
    }

    static formatTypeName = function(type: symbol) {
        return Result.typeNames.get(type);
    }

    static formatTypeNames = function(types: symbol[]) {
        let strings = types.map(type => Result.formatTypeName(type));

        if (strings.length == 1) return strings[0];

        let commaStrings = strings.slice(0, strings.length - 1);
        let finalString = strings[strings.length - 1];

        return commaStrings.join(", ") + " or " + finalString;
    }
}

interface Environment
{
    previousPosition: { x: number, y: number },
    currentPosition: { x: number, y: number },
    assetKey: string
}

class EvaluationContext
{
    public environment: Environment;
    public result: Result;
    public resultsByNodeId: Map<number, Result>;
    public visitedRoots: Set<number>;
    public otherGeofenceDepth: number;
    public nodeStorage: Map<number, any>;

    constructor(environment: Environment) {
        this.environment = environment;
        this.result = Result.empty;
        this.resultsByNodeId = new Map();
        this.visitedRoots = new Set();
        this.otherGeofenceDepth = 0;
        this.nodeStorage = new Map();
    }

    checkForInfiniteRecursion(rootNode: Expression) {
        if (this.visitedRoots.has(rootNode.id)) {
            throw new EvaluationError(rootNode, "A loop in a nested fragment was found.");
        } else {
            this.visitedRoots.add(rootNode.id);
        }
    }
}

export function evaluateGeofence(geofence: Geofence, environment: Environment, previousContext: EvaluationContext) {
    let context = new EvaluationContext(environment);

    if (previousContext != null) context.nodeStorage = previousContext.nodeStorage;

    if (geofence.kind == "template") throw new Error("A template geofence can not be evaluated directly.");

    let templateGeofence = geofence.template != null ? geofences.findGeofence(geofence.template) : null;

    context.result = evaluateInternal(geofence.root as Expression, context);

    if (context.result.type == Result.groupingType) {
        buildFlattenedEvaluationsRecursively(context.result.value as GroupingEvaluation, null);
    }

    return context;
}

function evaluateInternal(node: Expression, context: EvaluationContext) {
    let result = node.evaluateNode(context);

    context.resultsByNodeId.set(node.id, result);

    return result;
}

let nextExpressionId = 0;

export abstract class Expression
{
    public id: number;
    public properties: { [name: string]: any };

    constructor()
    {
        this.id = nextExpressionId++;
        this.properties = {};

        this.resetToDefaults();
    }

    abstract evaluateNode(context: EvaluationContext): Result;

    get name() { return "Expression"; }

    couldAdd() {
        return false;
    }

    resetToDefaults() {
    }

    getStorage(context: EvaluationContext, defaultValue: any) {
        let storage = context.nodeStorage.get(this.id);

        if (storage === undefined) {
            storage = defaultValue;
            
            context.nodeStorage.set(this.id, storage);
        }

        return storage;
    }

    serializeNode(intoParentElement: HTMLElement) {
        var element = intoParentElement.ownerDocument.createElement(this.name);

        for (let [key, value] of Object.entries(this.properties)) {
            element.setAttribute(key, String(value));
        }

        intoParentElement.appendChild(element);

        return element;
    }

    serialize() {
        let xml = document.implementation.createDocument("", "", null);
        let xmlSerializer = new XMLSerializer();

        let root = xml.createElement("Nodes");
        xml.appendChild(root);

        this.serializeNode(root);

        let indentedXml = this.indentXmlDocument(xml);

        return xmlSerializer.serializeToString(indentedXml);
    }

    indentXmlDocument(xml: Document) {
        var indentingXslt = new DOMParser().parseFromString(
            '<xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform">' +
            '  <xsl:strip-space elements="*" />' +
            '  <xsl:template match="para[content-style][not(text())]">' +
            '    <xsl:value-of select="normalize-space(.)"/>' +
            '  </xsl:template>' +
            '  <xsl:template match="node()|@*">' +
            '    <xsl:copy><xsl:apply-templates select="node()|@*"/></xsl:copy>' +
            '  </xsl:template>' +
            '  <xsl:output indent="yes" />' +
            '</xsl:stylesheet>', 'application/xml');

        var processor = new XSLTProcessor();    
        processor.importStylesheet(indentingXslt);

        return processor.transformToDocument(xml);
    }
}

interface EvaluatedChildren
{
    node: Expression;
    ordered: Result[];
    ofType(type: symbol): Result[];
    singleGeometry : Result;
    evaluatedLevels(initial: boolean, fold: (left: boolean, right: boolean) => boolean, empty?: boolean): boolean;
    evaluatedGeometries(
        before: ((geometry: geometries.JstsGeometry) => geometries.JstsGeometry) | null, 
        fold: (left: geometries.JstsGeometry, right: geometries.JstsGeometry) => geometries.JstsGeometry, 
        after: ((geometry: geometries.JstsGeometry) => geometries.JstsGeometry) | null): Result;
}

export abstract class ContainerExpression extends Expression
{
    public children: Expression[];

    constructor()
    {
        super();

        this.children = [];
    }

    get name() { return "ContainerExpression"; }

    couldAdd() {
        return true;
    }

    getPreviousSibling(child: Expression) {
        let index = this.children.indexOf(child);

        if (index <= 0) return null;

        return this.children[index - 1];
    }

    getLastChild() {
        if (this.children.length == 0) return null;

        return this.children[this.children.length - 1];
    }

    add(child: Expression) {
        this.children.push(child);
    }

    insert(child: Expression, after: Expression | null = null) {
        let index = after != null ? this.children.indexOf(after) : -1;

        this.children.splice(index + 1, 0, child);
    }

    delete(child: Expression) {
        let index = this.children.indexOf(child);

        if (index != -1) {
            this.children.splice(index, 1);

            return true;
        } else {
            return false;
        }
    }

    replace(oldChild: Expression, newChild: Expression) {
        let index = this.children.indexOf(oldChild);

        if (index != -1) {
            this.children[index] = newChild;

            return true;
        } else {
            return false;
        }
    }

    _evaluateChildren(context: EvaluationContext, ...types: symbol[]): EvaluatedChildren {
        let ordered: Result[] = [];

        let validateType: (result: Result) => void = result => {
            if (types.indexOf(result.type) == -1) {
                throw new EvaluationError(this, 
                    `A child "${Result.formatTypeName(result.type)}" item is not one of the expected item types (${Result.formatTypeNames(types)}).`);
            }
        };

        for (let child of this.children) {
            let result = evaluateInternal(child, context);

            if (result.type == Result.emptyType) continue;

            if (result.type != Result.spreadType) {
                validateType(result);

                ordered.push(result);
            } else {
                for (let childResult of result.value as Result[]) {
                    validateType(childResult);

                    ordered.push(childResult);
                }
            }
        }

        return {
            node: this,
            ordered,
            ofType(type: symbol) {
                return this.ordered.filter(candidate => candidate.type == type);
            },
            get singleGeometry() {
                let geometries = this.ofType(Result.geometryType);

                if (geometries.length == 0) return Result.geometryFromEmpty();
                if (geometries.length > 1) throw new EvaluationError(this.node, 
                    "A single geometry was expected. Use geometry combinations to make a single geometry.");

                return geometries[0];
            },
            evaluatedLevels(initial: boolean, fold: (left: boolean, right: boolean) => boolean, empty?: boolean): boolean {
                let levels = this.ofType(Result.levelType);

                if (levels.length == 0 && empty !== undefined) return empty;

                let accumulator = initial;

                for (let level of levels) {
                    accumulator = fold(accumulator, level.value == Result.assertedValue);
                }

                return accumulator;
            },
            evaluatedGeometries(
              before: ((geometry: geometries.JstsGeometry) => geometries.JstsGeometry) | null, 
              fold: (left: geometries.JstsGeometry, right: geometries.JstsGeometry) => geometries.JstsGeometry, 
              after: ((geometry: geometries.JstsGeometry) => geometries.JstsGeometry) | null): Result {
                let accumulator : geometries.JstsGeometry | null = null;

                for (let childResult of this.ofType(Result.geometryType)) {
                    if (childResult.type == Result.emptyType) continue;

                    let childRegion = childResult.value as geometries.Region;

                    let beforeFold = before != null ? before(childRegion.geometry) : childRegion.geometry;

                    if (accumulator == null) {
                        accumulator = beforeFold;
                    } else {
                        accumulator = fold(accumulator, beforeFold);
                    }
                }

                if (accumulator == null) return Result.empty;

                let beforeReturn = after != null ? after(accumulator) : accumulator;

                return Result.fromGeometry(beforeReturn);
            }
        };
    }

    serializeNode(intoParentElement: HTMLElement) {
        let element = super.serializeNode(intoParentElement);

        for (let child of this.children) child.serializeNode(element);

        return element;
    }
}

interface GroupingGeometries
{
    crossesIntoGeometry: boolean;
    crossesOutOfGeometry: boolean;
    insideOfGeometry: boolean;
    outsideOfGeometry: boolean;
}

interface GroupingValues
{
    geometries: GroupingGeometries | null,
    levels: boolean | null,
    edges: boolean | null,
    assets: symbol | null
}

interface ActionEvaluation
{
    action: Action,
    grouping: GroupingEvaluation
}

interface GroupingEvaluation
{
    primaryType: symbol,

    flattened: GroupingValues,
    evaluated: GroupingValues

    groupings: Result[],
    actions: Result[],
    geometries: Result[]
}

function mergeAssets(left: symbol | null, right: symbol | null): symbol | null {
    if (left === null && right === null) return null;

    if (left == null) return right;
    if (right == null) return left;

    if (left == Result.assetUnmatched) return right;
    if (right == Result.assetUnmatched) return left;

    if (left == Result.assetExcluded || right == Result.assetExcluded) return Result.assetExcluded;

    return left;
};

export function buildFlattenedEvaluationsRecursively(grouping: GroupingEvaluation, parentGrouping: GroupingEvaluation | null)
{
    const mergeGeometries = (left: GroupingGeometries | null, right: GroupingGeometries | null): GroupingGeometries | null => {
        if (left == null && right == null) return null;

        if (left == null) return Object.assign({}, right);
        if (right == null) return Object.assign({}, left);

        return {
            crossesIntoGeometry: left.crossesIntoGeometry || right.crossesIntoGeometry,
            crossesOutOfGeometry: left.crossesOutOfGeometry || right.crossesOutOfGeometry,
            insideOfGeometry: left.insideOfGeometry || right.insideOfGeometry,
            outsideOfGeometry: left.outsideOfGeometry || right.outsideOfGeometry
        };
    };

    const mergeNullableBooleans = (left: boolean | null, right: boolean | null): boolean | null => {
        if (left === null && right === null) return null;

        if (left == null) return right;
        if (right == null) return left;

        return left || right;
    };

    if (parentGrouping != null) {
        grouping.flattened = {
            geometries: mergeGeometries(parentGrouping.flattened.geometries, grouping.evaluated.geometries),
            levels: mergeNullableBooleans(parentGrouping.flattened.levels, grouping.evaluated.levels),
            edges: mergeNullableBooleans(parentGrouping.flattened.edges, grouping.evaluated.edges),
            assets: mergeAssets(parentGrouping.flattened.assets, grouping.evaluated.assets)
        };
    } else {
        grouping.flattened = grouping.evaluated;
    }

    if (grouping.flattened.geometries != null && grouping.flattened.edges != null) {
        throw new EvaluationError(null,
            "A grouping or task has had both edge and geometry items introduced during flattening.");
    }

    for (let childGrouping of grouping.groupings) {
        buildFlattenedEvaluationsRecursively(childGrouping.value as GroupingEvaluation, grouping);
    }
}

function flattenGroupsRecursively(into: GroupingEvaluation[], grouping: GroupingEvaluation)
{
    into.push(grouping);

    for (let childGrouping of grouping.groupings) {
        flattenGroupsRecursively(into, childGrouping.value as GroupingEvaluation);
    }
}

export function extractActions(grouping: GroupingEvaluation)
{
    let actions = [];
    let groupings: GroupingEvaluation[] = [];
    
    flattenGroupsRecursively(groupings, grouping);

    for (let grouping of groupings) {
        for (let action of grouping.actions) {
            actions.push({
                action: action.value,
                grouping
            });
        }
    }

    return actions;
}

function buildGroupingResult(node: Expression, childResults: EvaluatedChildren, environment: Environment): Result
{
    let geometries = childResults.ofType(Result.geometryType);
    let edges = childResults.ofType(Result.edgeType);
    let levels = childResults.ofType(Result.levelType);
    let assets = childResults.ofType(Result.assetsType);
    let groupings = childResults.ofType(Result.groupingType);
    let actions = childResults.ofType(Result.actionType);

    let value: GroupingEvaluation = {
        primaryType: Result.levelType,

        evaluated: {
            geometries: null,
            levels: true,
            edges: null,
            assets: null,
        },

        flattened: {
            geometries: null,
            levels: true,
            edges: null,
            assets: null,
        },

        geometries, // (For painting geofences)

        groupings,
        actions
    };

    if (geometries.length > 0 && edges.length > 0) {
        throw new EvaluationError(node,
            "A grouping or task should not contain both edge and geometry items.");
    }

    if (groupings.length > 0 && actions.length > 0) {
        throw new EvaluationError(node,
            "A grouping or task should not contain actions if it contains other groupings.");
    }

    if (geometries.length > 0) {
        value.primaryType = Result.geometryType;
    } 
    
    if (edges.length > 0) {
        value.primaryType = Result.edgeType;
    } 

    for (let geometry of geometries) {
        if (value.evaluated.geometries == null) {
            value.evaluated.geometries = {
                crossesIntoGeometry: false,
                crossesOutOfGeometry: false,
                insideOfGeometry: false,
                outsideOfGeometry: false
            };
        }

        let region = geometry.value as geometries.Region;
        let before = region.contains(environment.previousPosition);
        let after = region.contains(environment.currentPosition);

        value.evaluated.geometries.crossesIntoGeometry = value.evaluated.geometries.crossesIntoGeometry || (!before && after);
        value.evaluated.geometries.crossesOutOfGeometry = value.evaluated.geometries.crossesOutOfGeometry || (before && !after);
        value.evaluated.geometries.insideOfGeometry = value.evaluated.geometries.insideOfGeometry || (after);
        value.evaluated.geometries.outsideOfGeometry = value.evaluated.geometries.outsideOfGeometry || (!after);
    }

    for (let level of levels) {
        if (level.value != Result.assertedValue) value.evaluated.levels = false;
    }

    for (let edge of edges) {
        if (value.evaluated.edges == null) value.evaluated.edges = false;

        if (edge.value == Result.pulsedValue) value.evaluated.edges = true;
    }

    for (let asset of assets) {
        value.evaluated.assets = mergeAssets(value.evaluated.assets, asset.value as symbol);
    }

    return new Result(Result.groupingType, value)
}

export class Root extends ContainerExpression
{
    get description() { return "Root"; }
    get name() { return "Root"; }

    evaluateRawRootNode(context: EvaluationContext): Result {
        context.checkForInfiniteRecursion(this);

        let childResults = this._evaluateChildren(context, 
            Result.edgeType, Result.levelType, Result.geometryType, Result.groupingType, Result.assetsType, Result.actionType);

        if (context.otherGeofenceDepth == 0) {
            return buildGroupingResult(this, childResults, context.environment);
        } else {
            return new Result(Result.spreadType, childResults.ordered);
        }
    }

    evaluateNode(context: EvaluationContext) {
        try {
            return this.evaluateRawRootNode(context);
        } catch (e) {
            if (e.isEvaluationError) {
                return new Result(Result.errorType, e);
            } else {
                throw e;
            }
        }
    }
}

// Geometry:
export class Geometry extends Expression
{
    get description() { return "Geometry"; }
    get name() { return "Geometry"; }

    resetToDefaults() {
        this.properties.geometry = "triangle";
    }

    evaluateNode(context: EvaluationContext) {
        let geometry = geometries.defaultRegionStore.regions[this.properties.geometry];
        let result = new Result(Result.geometryType, geometry);

        return result;
    }
}

// Geometry Combinations:
export class Buffer extends ContainerExpression
{
    get description() { return "Buffer"; }
    get name() { return "Buffer"; }

    resetToDefaults() {
        this.properties.outerDistance = 20;
        this.properties.innerDistance = 0;
        this.properties.kind = "solid";
    }

    evaluateNode(context: EvaluationContext) {
        let buffering: (geometry: geometries.JstsGeometry) => geometries.JstsGeometry;

        if (isNaN(this.properties.outerDistance) || isNaN(this.properties.outerDistance)) {
            return Result.empty;
        }

        if (this.properties.kind == "solid") {
            buffering = geometry => geometry.buffer(this.properties.outerDistance);
        } else {
            buffering = geometry => geometry.buffer(this.properties.outerDistance).difference(
                geometry.buffer(Math.min(this.properties.innerDistance, this.properties.outerDistance)));
        }

        let childResults = this._evaluateChildren(context, Result.geometryType);

        return childResults.evaluatedGeometries(
            null, (left, right) => left.union(right), buffering);
    }
}

export class Union extends ContainerExpression
{
    get description() { return "Union"; }
    get name() { return "Union"; }

    evaluateNode(context: EvaluationContext) {
        let childResults = this._evaluateChildren(context, Result.geometryType);

        return childResults.evaluatedGeometries(
            null, (left, right) => left.union(right), null);
    }
}

export class Intersection extends ContainerExpression
{
    get description() { return "Intersection"; }
    get name() { return "Intersection"; }

    evaluateNode(context: EvaluationContext) {
        let childResults = this._evaluateChildren(context, Result.geometryType);

        return childResults.evaluatedGeometries(
            null, (left, right) => left.intersection(right), null);
    }
}

export class Difference extends ContainerExpression
{
    get description() { return "Difference"; }
    get name() { return "Difference"; }

    evaluateNode(context: EvaluationContext) {
        let childResults = this._evaluateChildren(context, Result.geometryType);

        return childResults.evaluatedGeometries(
            null, (left, right) => left.difference(right), null);
    }
}

export class ConditionalGeometry extends ContainerExpression
{
    get description() { return "Conditional Geometry"; }
    get name() { return "ConditionalGeometry"; }

    evaluateNode(context: EvaluationContext) {
        let childResults = this._evaluateChildren(context, Result.geometryType, Result.levelType);

        return childResults.evaluatedLevels(false, (left, right) => left || right, true) ? childResults.singleGeometry : Result.geometryFromEmpty();
    }
}

// Geometry Interactions:
abstract class GeometryInteractionExpression extends ContainerExpression
{
    evaluateNode(context: EvaluationContext) {
        let childResults = this._evaluateChildren(context, Result.geometryType);

        return this.evaluateInteraction(context, childResults.singleGeometry.value as geometries.Region);
    }

    abstract evaluateInteraction(context: EvaluationContext, geometry: geometries.Region): Result;
}

export class CrossesIntoGeometry extends GeometryInteractionExpression
{
    get description() { return "Crosses into Geometry"; }
    get name() { return "CrossesIntoGeometry"; }

    evaluateInteraction(context: EvaluationContext, geometry: geometries.Region) {
        let before = geometry.contains(context.environment.previousPosition);
        let after = geometry.contains(context.environment.currentPosition);

        return Result.pulseFrom(!before && after);
    }
}

export class CrossesOutOfGeometry extends GeometryInteractionExpression
{
    get description() { return "Crosses out of Geometry"; }
    get name() { return "CrossesOutOfGeometry"; }

    evaluateInteraction(context: EvaluationContext, geometry: geometries.Region) {
        let before = geometry.contains(context.environment.previousPosition);
        let after = geometry.contains(context.environment.currentPosition);

        return Result.pulseFrom(before && !after);
    }
}

export class InsideOfGeometry extends GeometryInteractionExpression
{
    get description() { return "Inside of Geometry"; }
    get name() { return "InsideOfGeometry"; }

    evaluateInteraction(context: EvaluationContext, geometry: geometries.Region) {
        let inside = geometry.contains(context.environment.currentPosition);

        return Result.levelFrom(inside);
    }
}

export class OutsideOfGeometry extends GeometryInteractionExpression
{
    get description() { return "Outside of Geometry"; }
    get name() { return "OutsideOfGeometry"; }

    evaluateInteraction(context: EvaluationContext, geometry: geometries.Region) {
        let inside = geometry.contains(context.environment.currentPosition);

        return Result.levelFrom(!inside);
    }
}

// Asset:
export class Asset extends Expression
{
    get description() { return "Asset"; }
    get name() { return "Asset"; }

    resetToDefaults() {
        this.properties.assetKey = "red";
    }

    evaluateNode(context: EvaluationContext) {
        return Result.assetFrom(this.properties.assetKey == context.environment.assetKey);
    }
}

export class AssetClass extends Expression
{
    get description() { return "Asset Class"; }
    get name() { return "AssetClass"; }

    resetToDefaults() {
        this.properties.classKey = "coloured-dots";
    }

    evaluateNode(context: EvaluationContext) {
        return Result.assetFrom(this.properties.classKey == "coloured-dots");
    }
}

export class AssetGroup extends Expression
{
    get description() { return "Asset Group"; }
    get name() { return "AssetGroup"; }

    resetToDefaults() {
        this.properties.groupKey = "red,yellow";
    }

    evaluateNode(context: EvaluationContext) {
        return Result.assetFrom(this.properties.groupKey.indexOf(context.environment.assetKey) != -1);
    }
}

export class AllAssets extends Expression
{
    get description() { return "All Assets"; }
    get name() { return "AllAssets"; }

    evaluateNode(context: EvaluationContext) {
        return Result.assetFrom(true);
    }
}

// Conditions:
export class TimeOfDay extends Expression
{
    get description() { return "Time of Day"; }
    get name() { return "TimeOfDay"; }

    resetToDefaults() {
        this.properties.fromTime = "9:00AM";
        this.properties.toTime = "5:00PM";
        this.properties.days = 0x7f;
    }

    evaluateNode(context: EvaluationContext) {
        return Result.levelFrom(true);
    }
}

export class TimeAndDateRange extends Expression
{
    get description() { return "Time and Date Range"; }
    get name() { return "TimeAndDateRange"; }

    resetToDefaults() {
        this.properties.rangeKind = "single-day";
        this.properties.fromTimeAndDate = "21/9/2020";
        this.properties.toTimeAndDate = "";
    }

    evaluateNode(context: EvaluationContext) {
        return Result.levelFrom(true);
    }
}

export class SpeedRange extends Expression
{
    get description() { return "Speed Range"; }
    get name() { return "SpeedRange"; }

    evaluateNode(context: EvaluationContext) {
        return Result.levelFrom(true);
    }
}

interface LevelToPulseStorage
{
    previous: boolean | null,
    since: Date,
    inhibited: boolean
}

// Level to Pulse
export abstract class LevelToPulseExpression extends ContainerExpression
{
    evaluateNode(context: EvaluationContext) {
        let storage = this.getStorage(context, { previous: null, since: null, inhibited: false }) as LevelToPulseStorage;
        let previous = storage.previous;

        let childResults = this._evaluateChildren(context, Result.levelType);
        let next = childResults.evaluatedLevels(true, (left, right) => left && right);

        if (previous != next) {
            storage.previous = next;
            storage.since = new Date();
            storage.inhibited = false;
        }

        let since: Date = storage.since ?? new Date();

        if (previous == null || storage.inhibited) return Result.pulseFrom(false);

        let result = this.evaluatePulse(previous, next, (new Date().valueOf() - since.valueOf()) / 1000);

        if (result) storage.inhibited = true;

        return Result.pulseFrom(result);
    }
    
    abstract evaluatePulse(previous: boolean, next: boolean, duration: number): boolean;
}

export class OnStarted extends LevelToPulseExpression
{
    get description() { return "On Started"; }
    get name() { return "OnStarted"; }

    evaluatePulse(previous: boolean, next: boolean, duration: number): boolean {
        return !previous && next;
    }
}

export class OnFinished extends LevelToPulseExpression
{
    get description() { return "On Finished"; }
    get name() { return "OnFinished"; }

    evaluatePulse(previous: boolean, next: boolean, duration: number): boolean {
        return previous && next;
    }
}

export class LongerThan extends LevelToPulseExpression
{
    get description() { return "Longer Than"; }
    get name() { return "LongerThan"; }

    evaluatePulse(previous: boolean, next: boolean, duration: number): boolean {
        return next && duration > 3;
    }

    resetToDefaults() {
        this.properties.duration = 3;
    }
}

// Boolean
export class And extends ContainerExpression
{
    get description() { return "And"; }
    get name() { return "And"; }

    evaluateNode(context: EvaluationContext) {
        let childResults = this._evaluateChildren(context, Result.levelType);

        return Result.levelFrom(childResults.evaluatedLevels(true, (left, right) => left && right));
    }
}

export class Or extends ContainerExpression
{
    get description() { return "Or"; }
    get name() { return "Or"; }

    evaluateNode(context: EvaluationContext) {
        let childResults = this._evaluateChildren(context, Result.edgeType, Result.levelType);

        return Result.levelFrom(childResults.evaluatedLevels(false, (left, right) => left || right, true));
    }
}

export class Not extends ContainerExpression
{
    get description() { return "Not"; }
    get name() { return "Not"; }

    evaluateNode(context: EvaluationContext) {
        let childResults = this._evaluateChildren(context, Result.levelType);

        return Result.levelFrom(!childResults.evaluatedLevels(false, (left, right) => left || right, true));
    }
}

// Other:
export class Fragment extends Expression
{
    get description() { return "Fragment"; }
    get name() { return "Fragment"; }

    resetToDefaults() {
        this.properties.geofence = null;
    }

    evaluateNode(context: EvaluationContext) {
        if (this.properties.geofence == null) throw new EvaluationError(this, "No fragment is selected.");

        let geofence = geofences.findGeofence(this.properties.geofence);

        if (geofence == null) throw new EvaluationError(this, "The selected fragment could not be found.");

        context.otherGeofenceDepth++;

        let result = geofence.root.evaluateRawRootNode(context);

        context.otherGeofenceDepth--;

        return result;
    }
}

export class Grouping extends ContainerExpression
{
    get description() { return "Grouping"; }
    get name() { return "Grouping"; }

    evaluateNode(context: EvaluationContext): Result {
        let childResults = this._evaluateChildren(context, 
            Result.edgeType, Result.levelType, Result.geometryType, Result.groupingType, Result.assetsType, Result.actionType);

        return buildGroupingResult(this, childResults, context.environment);
    }
}

export class Exclude extends ContainerExpression
{
    get description() { return "Exclude"; }
    get name() { return "Exclude"; }

    evaluateNode(context: EvaluationContext): Result {
        let childResults = this._evaluateChildren(context, Result.assetsType);

        let results: Result[] = [];

        for (let result of childResults.ofType(Result.assetsType)) {
            if (result.value == Result.assetIncluded) {
                results.push(new Result(Result.assetsType, Result.assetExcluded));
            } else if (result.value == Result.assetExcluded) {
                results.push(result);
            }
        }

        return new Result(Result.spreadType, results);
    }
}

export class Action extends ContainerExpression
{
    get description() { return "Action"; }
    get name() { return "Action"; }

    resetToDefaults() {
        this.properties.trigger = "crosses-into";
        this.properties.triggerDuration = 3;
        this.properties.message = "Hello!";
    }

    evaluateNode(context: EvaluationContext): Result {
        let childResults = this._evaluateChildren(context, 
            Result.edgeType, Result.levelType, Result.geometryType, Result.assetsType);

        let actionResult = new Result(Result.actionType, Object.assign({ id: this.id }, this.properties));

        childResults.ordered.push(actionResult);

        return buildGroupingResult(this, childResults, context.environment);
    }
}

export class TemplateField extends Expression
{
    get description() { return "Template Field"; }
    get name() { return "TemplateField"; }

    evaluateNode(context: EvaluationContext) {
        return Result.empty;
    }
}

export const nodesByTheme = [
    { name: "Geometry", constructors: [Geometry], advanced: false },
    { name: "Geometry Combinations", constructors: [Buffer, Union, Intersection, Difference, ConditionalGeometry], advanced: false },
    { name: "Geometry Interactions", constructors: [CrossesIntoGeometry, CrossesOutOfGeometry, InsideOfGeometry, OutsideOfGeometry], advanced: true },
    { name: "Linking", constructors: [Fragment], advanced: false },
    { name: "Asset", constructors: [Asset, AssetClass, AssetGroup, AllAssets, Exclude], advanced: false },
    { name: "Conditions", constructors: [TimeOfDay, TimeAndDateRange, SpeedRange], advanced: false },
    { name: "Level to Edge", constructors: [OnStarted, OnFinished, LongerThan], advanced: true },
    { name: "Boolean Combinations", constructors: [And, Or, Not], advanced: true },
    { name: "Actions", constructors: [Grouping, Action], advanced: false },
];
